import { Injectable } from '@angular/core';
import {
  BundleBuilderState,
  BundleItem,
  BundleLine,
  BundleLineDto,
  Category,
  CategoryType,
} from './bundle-builder.model';
import { map } from 'rxjs/operators';
import { Store } from '../../shared-core/services/store';
import { BundleBuilderApiService } from './bundle-builder-api.service';
import { BehaviorSubject } from 'rxjs';
@Injectable({ providedIn: 'root' })
export class BundleBuilderStore extends Store<BundleBuilderState> {
  public get cartBundle(): BundleLineDto[] {
    return (window as any).bundleLines;
  }

  public set cartBundle(val: BundleLineDto[]) {
    (window as any).bundleLines = val;
  }

  public get bundleDefinition(): BundleItem[] {
    return (window as any).bundleItems;
  }
  private set bundleDefinition(val: BundleItem[]) {
    (window as any).bundleItems = val;
  }

  public async init(bundleId: number): Promise<void> {
    if (!this.bundleDefinition || this.bundleDefinition.find((i) => i.BundleId != bundleId)) {
      this.bundleDefinition = await this.api.getBundleItemDefinitions(bundleId);
    }
    // we do not show free items
    this.bundleDefinition = this.bundleDefinition.filter((i) => !i.IsFree);
    let categoryNames = this.bundleDefinition.reduce((acc, def) => {
      if (!acc.includes(def.Category) && !def.IsAnchor) {
        acc.push(def.Category);
      }
      //reuse this loop to set default values on bundle definitions
      def.RequestedQuantity = 1;
      return acc;
    }, [] as string[]);
    let categories = categoryNames
      .sort((a, b) => (a < b ? -1 : 1))
      .map<Category>((name) => {
        return { name: name, id: name };
      });
    this.setState({
      ...this.state,
      categories: categories,
    });
  }

  /**
   *
   */
  constructor(private api: BundleBuilderApiService) {
    super(new BundleBuilderState());
  }
  public storeWaitingSubject = new BehaviorSubject<boolean>(false);

  public readonly anchor$ = this.state$.pipe(map((x) => x.anchor));

  public readonly bundle$ = this.state$.pipe(map((x) => x.bundle));

  public readonly categories$ = this.state$.pipe(map((x) => x.categories));

  public readonly currentCategory$ = this.state$.pipe(map((x) => x.currentCategory));

  public readonly filteredBundleItems$ = this.state$.pipe(map((x) => x.filteredBundleItems));

  public readonly modified$ = this.state$.pipe(map((x) => x.modified));

  public async addItem(item: BundleItem) {
    this.storeWaitingSubject.next(true);
    let bundleLines = [...this.state.bundle.lines];
    let line = bundleLines.find((i) => i.item.Webcode == item.Webcode);
    item.IsInCart = true;
    if (line) {
      line.quantity = item.RequestedQuantity;
    } else {
      bundleLines.push({ quantity: item.RequestedQuantity, item: item });
      bundleLines = bundleLines.sort((a, b) => (a.item.SellingPrice > b.item.SellingPrice ? -1 : 1));
    }
    const pricing = await this.api.getBundlePrice(bundleLines);
    const modified = this.isCartModified(bundleLines);
    this.setState({
      ...this.state,
      bundle: { price: pricing, lines: bundleLines },
      modified,
    });
    this.storeWaitingSubject.next(false);
  }

  public async setCartBundle(lines: BundleLine[]) {
    const pricing = await this.api.getBundlePrice(lines);
    this.setState({
      ...this.state,
      bundle: { lines: lines, price: pricing },
    });
  }

  public async setAnchor(item: BundleItem) {
    const anchor = this.state.bundle.lines.find((l) => l.item.Webcode == item.Webcode);
    if (anchor) {
      this.setState({
        ...this.state,
        anchor,
      });
      return;
    }
    const anchorline: BundleLine = { item: item, quantity: 1 };
    const lines = [...this.state.bundle.lines, anchorline];
    const pricing = await this.api.getBundlePrice(lines);
    const modified = this.isCartModified(lines);
    this.setState({
      ...this.state,
      anchor: anchorline,
      bundle: { price: pricing, lines: lines },
      modified,
    });
  }

  public async remove(line: BundleLine) {
    this.storeWaitingSubject.next(true);
    const bundleLines = this.state.bundle.lines.filter((i) => i.item.Webcode !== line.item.Webcode);
    const pricing = await this.api.getBundlePrice(bundleLines);
    const modified = this.isCartModified(bundleLines);
    this.setState({
      ...this.state,
      bundle: { price: pricing, lines: bundleLines },
      modified: modified,
    });
    this.storeWaitingSubject.next(false);
  }

  public setCategory(category?: Category) {
    let currentCategory = category ?? this.state.categories[0];
    const bundleItems = this.bundleDefinition
      .filter(
        (i) =>
          i.Webcode !== this.state.anchor.item.Webcode &&
          (i.Category == currentCategory.id ||
            (currentCategory.id == CategoryType.Free && i.IsFree) ||
            (currentCategory.id == CategoryType.Level1 && i.DiscountLevel == 1) ||
            (currentCategory.id == CategoryType.Level2 && i.DiscountLevel == 2) ||
            (currentCategory.id == CategoryType.Anchor && i.IsAnchor)
            && !i.IsAnchor) // do not display anchor items
      )
      .sort((a, b) => (a.Description > b.Description ? -1 : 1));
    this.setState({
      ...this.state,
      filteredBundleItems: bundleItems,
      currentCategory: currentCategory,
    });
  }

  public isCartModified(lines: BundleLine[]) {
    let hasChanges = false;
    for (var i = 0; i < lines.length; i++) {
      const bd = lines[i];
      const cartItem = this.cartBundle.find((ci) => ci.Webcode == bd.item.Webcode);
      // added or qty changed
      bd.item.HasUnsavedChanges = !cartItem || cartItem.Quantity !== bd.item.RequestedQuantity;
      if (bd.item.HasUnsavedChanges) {
        hasChanges = bd.item.HasUnsavedChanges;
      }
    }

    for (var i = 0; i < this.cartBundle.length; i++) {
      const cartItem = this.cartBundle[i];
      const bundleItem = lines.find((ci) => ci.item.Webcode == cartItem.Webcode);
      // item removed
      if (!bundleItem) {
        hasChanges = true;
        break;
      }
    }
    return hasChanges;
  }
}
